/**
 * Bobo, the Happy mascot, for React Native — a port of the admin `Bobo/Bobo.js` (same 160x170 art, same gradients).
 *
 * The art is split into stacked SVG layers (tail, body, arms, head parts, eyes, lids, ...). `react-native-svg`
 * cannot animate child groups with CSS, so every part that moves lives in its own absolutely positioned
 * `Animated.View` and the UI-thread reanimated timelines drive that view's transform (breathing, tail wag,
 * blink, wave, squash-and-stretch hops, a 3D-ish twirl, sit/stand, dance ...). The body timelines are the CSS
 * keyframes of the admin stylesheet as data (`boboMotion.js`).
 *
 * `Bobo` is controlled and purely presentational; `BoboMascot` adds the idle cycle, touch reactions and a bubble.
 * With the OS "reduce motion" setting every loop is off and states simply switch.
 *
 * @module components/bobo/Bobo
 */

import React, {memo, useEffect, useId, useRef} from 'react';
import {Text, View} from 'react-native';
import Svg, {
    Circle, ClipPath, Defs, Ellipse, G, LinearGradient, Path, RadialGradient, Stop,
} from 'react-native-svg';
import Animated, {
    Easing, cancelAnimation, useAnimatedStyle, useReducedMotion, useSharedValue, withDelay, withRepeat,
    withSequence, withTiming,
} from 'react-native-reanimated';
import {deriveBoboPose, moodToProps} from './boboLogic';
import {MOTIONS, evaluate, sample} from './boboMotion';

/** Extra room around the 160x170 art (SVG units) so hops, bubbles and particles are not clipped. */
const PU = 26;
const PV = 44;
const ART_W = 160;
const ART_H = 170;

const HEART = 'M0 7C-9 1-11-5-6-8C-3-9.5 0-7 0-5C0-7 3-9.5 6-8C11-5 9 1 0 7Z';
const SPARK = 'M0-7L2-2L7 0L2 2L0 7L-2 2L-7 0L-2-2Z';
const STAR = 'M0-7L2.1-2.5L7-2.2L3.3 1.2L4.4 6L0 3.5L-4.4 6L-3.3 1.2L-7-2.2L-2.1-2.5Z';
const SPIRAL = 'M0 0a2 2 0 0 1 4 0a4 4 0 0 1-8 0a6 6 0 0 1 12 0a8 8 0 0 1-16 0';
const MOUTH_OPEN = 'M70 94c0 9 20 9 20 0z';
const BODY_PATH = 'M80 54c-34 0-52 24-52 56 0 30 20 46 52 46s52-16 52-46c0-32-18-56-52-56z';

/** Where the particles of a burst start and drift to (SVG units), around Bobo's head. */
const PARTICLES = Object.freeze([
    {x: 46, y: 50, dx: -26, dy: -34, delay: 0, size: 1},
    {x: 114, y: 50, dx: 26, dy: -38, delay: 80, size: 1.1},
    {x: 80, y: 36, dx: 2, dy: -46, delay: 160, size: 1.25},
    {x: 62, y: 44, dx: -12, dy: -28, delay: 240, size: 0.8},
    {x: 98, y: 44, dx: 14, dy: -30, delay: 320, size: 0.85},
]);

const PARTICLE_STOPS = Object.freeze([0, 0.18, 1]);

/**
 * Looping 0..1 shared value (yoyo by default), stopped when disabled.
 *
 * @param {number} duration One way, ms.
 * @param {boolean} enabled Run the loop.
 * @param {Object} [options] Options.
 * @param {boolean} [options.reverse] Yoyo (default) or restart.
 * @param {boolean} [options.linear] Linear easing instead of a sine ease.
 * @returns {Object} Reanimated shared value.
 */
function useLoop(duration, enabled, {reverse = true, linear = false} = {}) {
    const value = useSharedValue(0);

    useEffect(() => {
        if (!enabled) {
            cancelAnimation(value);
            value.value = 0;

            return undefined;
        }
        value.value = 0;
        value.value = withRepeat(
            withTiming(1, {duration, easing: linear ? Easing.linear : Easing.inOut(Easing.sin)}),
            -1,
            reverse,
        );

        return () => cancelAnimation(value);
    }, [duration, enabled, linear, reverse, value]);

    return value;
}

/**
 * Eyelid openness loop: a long open phase and an occasional double blink (the admin `bobo-blink`, 6 s).
 *
 * @param {boolean} enabled Run the loop.
 * @returns {Object} Shared value, 0 = open, 1 = closed.
 */
function useBlink(enabled) {
    const value = useSharedValue(0);

    useEffect(() => {
        if (!enabled) {
            cancelAnimation(value);
            value.value = 0;

            return undefined;
        }
        value.value = withRepeat(withSequence(
            withTiming(0, {duration: 5400}),
            withTiming(1, {duration: 90}),
            withTiming(0, {duration: 90}),
            withTiming(1, {duration: 110}),
            withTiming(0, {duration: 110}),
        ), -1);

        return () => cancelAnimation(value);
    }, [enabled, value]);

    return value;
}

/**
 * Runs the body timeline of the current `motion` and returns the shared values the layers read.
 *
 * @param {string} motion Motion name ('' = rest), see `boboMotion.js`.
 * @param {boolean} reduced OS reduce-motion is on.
 * @returns {{id: Object, t: Object}} Shared motion index and progress.
 */
function useMotion(motion, reduced) {
    const id = useSharedValue(0);
    const t = useSharedValue(0);
    const previous = useRef('');

    useEffect(() => {
        const def = MOTIONS[motion] || MOTIONS[''];
        const old = MOTIONS[previous.current] || MOTIONS[''];

        previous.current = motion;
        if (reduced) {
            id.value = def.index;
            t.value = def.hold ? 1 : 0;

            return;
        }
        if (!motion) {
            if (old.hold || old.repeat === -1) {
                t.value = withTiming(0, {duration: 350, easing: Easing.out(Easing.cubic)}, (finished) => {
                    'worklet';
                    if (finished) {
                        id.value = 0;
                    }
                });
            }

            return;
        }
        if (old.holdKey && old.holdKey === def.holdKey && t.value >= 0.99) {
            id.value = def.index;

            return;
        }
        const hold = def.hold;
        const forever = def.repeat === -1;
        const settle = (finished) => {
            'worklet';
            if (finished && !hold && !forever) {
                id.value = 0;
                t.value = 0;
            }
        };
        const run = withTiming(1, {duration: def.ms, easing: Easing.linear});

        cancelAnimation(t);
        id.value = def.index;
        t.value = 0;
        t.value = def.repeat !== 1 ? withRepeat(run, def.repeat, false, settle) : withTiming(1, {duration: def.ms, easing: Easing.linear}, settle);
    }, [motion, reduced, id, t]);

    return {id, t};
}

/** The radial / linear gradients of the art (ids are unique per mascot so several can share a screen on web). */
const BoboDefs = memo(function BoboDefs({uid}) {
    return (
    <Defs>
        <RadialGradient id={`${uid}-body`} cx="0.4" cy="0.24" r="0.85" fx="0.36" fy="0.16">
            <Stop offset="0" stopColor="#FF9A45" />
            <Stop offset="0.5" stopColor="#F97316" />
            <Stop offset="1" stopColor="#B83E0A" />
        </RadialGradient>
        <RadialGradient id={`${uid}-belly`} cx="0.5" cy="0.3" r="0.8">
            <Stop offset="0" stopColor="#FFF7ED" />
            <Stop offset="0.6" stopColor="#FED7AA" />
            <Stop offset="1" stopColor="#F9A762" />
        </RadialGradient>
        <RadialGradient id={`${uid}-foot`} cx="0.4" cy="0.25" r="0.9">
            <Stop offset="0" stopColor="#F26A1F" />
            <Stop offset="1" stopColor="#8F300B" />
        </RadialGradient>
        <RadialGradient id={`${uid}-hand`} cx="0.35" cy="0.3" r="0.85">
            <Stop offset="0" stopColor="#FFF7ED" />
            <Stop offset="0.65" stopColor="#FED7AA" />
            <Stop offset="1" stopColor="#F9A762" />
        </RadialGradient>
        <LinearGradient id={`${uid}-tail`} x1="0" y1="0" x2="1" y2="1">
            <Stop offset="0" stopColor="#F26A1F" />
            <Stop offset="1" stopColor="#A83A09" />
        </LinearGradient>
        <RadialGradient id={`${uid}-eye`} cx="0.5" cy="0.38" r="0.7">
            <Stop offset="0.6" stopColor="#FFFFFF" />
            <Stop offset="1" stopColor="#F3DCCB" />
        </RadialGradient>
        <RadialGradient id={`${uid}-pupil`} cx="0.4" cy="0.35" r="0.8">
            <Stop offset="0" stopColor="#4B3626" />
            <Stop offset="0.55" stopColor="#1B1613" />
            <Stop offset="1" stopColor="#0A0908" />
        </RadialGradient>
        <RadialGradient id={`${uid}-cheek`} cx="0.5" cy="0.5" r="0.5">
            <Stop offset="0" stopColor="#FB5C78" stopOpacity="0.75" />
            <Stop offset="1" stopColor="#FB5C78" stopOpacity="0" />
        </RadialGradient>
        <RadialGradient id={`${uid}-heart`} cx="0.35" cy="0.3" r="0.9">
            <Stop offset="0" stopColor="#FDA4AF" />
            <Stop offset="1" stopColor="#E11D48" />
        </RadialGradient>
        <LinearGradient id={`${uid}-leafA`} x1="0" y1="0" x2="1" y2="1">
            <Stop offset="0" stopColor="#B79CFA" />
            <Stop offset="1" stopColor="#7C3AED" />
        </LinearGradient>
        <LinearGradient id={`${uid}-leafB`} x1="0" y1="0" x2="1" y2="1">
            <Stop offset="0" stopColor="#FFE2C2" />
            <Stop offset="1" stopColor="#FB923C" />
        </LinearGradient>
        <LinearGradient id={`${uid}-rim`} x1="0" y1="0" x2="1" y2="1">
            <Stop offset="0" stopColor="#FFFFFF" stopOpacity="0.7" />
            <Stop offset="0.45" stopColor="#FFFFFF" stopOpacity="0" />
            <Stop offset="1" stopColor="#FFFFFF" stopOpacity="0.12" />
        </LinearGradient>
        <RadialGradient id={`${uid}-shadow`} cx="0.5" cy="0.5" r="0.5">
            <Stop offset="0" stopColor="#000000" stopOpacity="0.38" />
            <Stop offset="1" stopColor="#000000" stopOpacity="0" />
        </RadialGradient>
        <ClipPath id={`${uid}-mouth`}>
            <Path d={MOUTH_OPEN} />
        </ClipPath>
    </Defs>
    );
});

/**
 * One art layer: an absolutely positioned (animated) view holding a full-size SVG with Bobo's coordinate system.
 *
 * @param {Object} props Props.
 * @param {number} props.s Pixels per SVG unit.
 * @param {string} props.uid Gradient id prefix.
 * @param {Array<number>} [props.origin] Transform origin in SVG units.
 * @param {*} [props.style] Animated / static style.
 * @param {React.ReactNode} props.children SVG children.
 * @returns {JSX.Element} The layer.
 */
const Layer = ({s, uid, origin, style, children}) => (
    <Animated.View
        pointerEvents="none"
        style={[
            {position: 'absolute', left: -PU * s, top: -PV * s, width: (ART_W + 2 * PU) * s, height: (ART_H + 2 * PV) * s},
            origin ? {transformOrigin: `${(origin[0] + PU) * s}px ${(origin[1] + PV) * s}px`} : null,
            style,
        ]}
    >
        <Svg width="100%" height="100%" viewBox={`${-PU} ${-PV} ${ART_W + 2 * PU} ${ART_H + 2 * PV}`}>
            <BoboDefs uid={uid} />
            {children}
        </Svg>
    </Animated.View>
);

/**
 * An art-sized frame without SVG, used to group layers that move together (head, body, mood).
 *
 * @param {Object} props Props.
 * @param {number} props.s Pixels per SVG unit.
 * @param {Array<number>} [props.origin] Transform origin in SVG units.
 * @param {*} [props.style] Animated / static style.
 * @param {React.ReactNode} props.children Layers.
 * @returns {JSX.Element} The frame.
 */
const Frame = ({s, origin, style, children}) => (
    <Animated.View
        pointerEvents="none"
        style={[
            {position: 'absolute', left: 0, top: 0, width: ART_W * s, height: ART_H * s},
            origin ? {transformOrigin: `${origin[0] * s}px ${origin[1] * s}px`} : null,
            style,
        ]}
    >
        {children}
    </Animated.View>
);

/**
 * A single particle of a burst (heart, spark or star) that floats up and fades.
 *
 * @param {Object} props Props.
 * @param {Object} props.spec One entry of `PARTICLES`.
 * @param {string} props.kind 'heart' | 'spark' | 'star'.
 * @param {string} props.uid Gradient id prefix.
 * @param {number} props.s Pixels per SVG unit.
 * @param {boolean} props.reduced OS reduce-motion is on.
 * @returns {JSX.Element} The particle.
 */
const Particle = ({spec, kind, uid, s, reduced}) => {
    const p = useSharedValue(0);

    useEffect(() => {
        if (!reduced) {
            p.value = withDelay(spec.delay, withTiming(1, {duration: 1500, easing: Easing.out(Easing.quad)}));
        }

        return () => cancelAnimation(p);
    }, [p, reduced, spec.delay]);
    const style = useAnimatedStyle(() => {
        const k = PARTICLE_STOPS;
        const scale = sample(k, [0.2, spec.size, spec.size], p.value);

        return {
            opacity: p.value === 0 ? 0 : sample(k, [0, 1, 0], p.value),
            transform: [
                {translateX: sample(k, [0, spec.dx * 0.2, spec.dx], p.value) * s},
                {translateY: sample(k, [0, spec.dy * 0.25, spec.dy], p.value) * s},
                {scale},
                {rotate: `${sample(k, [0, 0, 24], p.value)}deg`},
            ],
        };
    });
    const shape = kind === 'heart' ? HEART : (kind === 'star' ? STAR : SPARK);
    const fill = kind === 'heart' ? `url(#${uid}-heart)` : (kind === 'star' ? '#FBBF24' : '#FDE68A');
    const box = 20 * s;

    return (
        <Animated.View
            pointerEvents="none"
            style={[{position: 'absolute', left: spec.x * s - box / 2, top: spec.y * s - box / 2, width: box, height: box}, style]}
        >
            <Svg width="100%" height="100%" viewBox="-10 -10 20 20">
                <BoboDefs uid={uid} />
                <Path d={shape} fill={fill} stroke="#FFFFFF" strokeWidth="1.2" strokeLinejoin="round" />
            </Svg>
        </Animated.View>
    );
};

/**
 * A continuously spinning view (dizzy spiral eyes, the orbiting stars).
 *
 * @param {Object} props Props.
 * @param {number} props.duration One turn, ms.
 * @param {boolean} [props.reverse] Spin counter-clockwise.
 * @param {boolean} props.enabled Run the spin.
 * @param {*} [props.style] Static style.
 * @param {React.ReactNode} props.children Content.
 * @returns {JSX.Element} The spinner.
 */
const Spinner = ({duration, reverse = false, enabled, style, children}) => {
    const turn = useLoop(duration, enabled, {reverse: false, linear: true});
    const animated = useAnimatedStyle(() => ({
        transform: [{rotate: `${(reverse ? -360 : 360) * turn.value}deg`}],
    }));

    return <Animated.View pointerEvents="none" style={[style, animated]}>{children}</Animated.View>;
};

/**
 * Bobo, drawn. Controlled: the parent decides the situation (`mood` / `action` / flags) and any touch `reaction`.
 *
 * @param {Object} props Props.
 * @param {number} [props.size] Width of the art in dp (height follows).
 * @param {(string|null)} [props.mood] 'happy' | 'worried' | 'thinking' | 'celebrate' | 'love' | 'surprised' | 'sleepy' | 'greet'.
 * @param {(string|null)} [props.action] Idle action from the idle machine.
 * @param {(string|null)} [props.reaction] Touch reaction: 'hop' | 'spin' | 'dizzy' | 'squish' | 'love'.
 * @param {Array<{id: number, kind: string}>} [props.bursts] Live particle bursts.
 * @param {boolean} [props.coverEyes] Cover the eyes.
 * @param {boolean} [props.hasError] Worried face.
 * @param {boolean} [props.celebrate] Jump for joy.
 * @param {boolean} [props.saving] Open mouth while saving.
 * @param {boolean} [props.greet] Wave and smile.
 * @param {boolean} [props.shake] One-shot shake.
 * @param {(string|null)} [props.expression] Forced face.
 * @param {{x: number, y: number}} [props.pupils] Pupil offset in SVG units.
 * @param {string} [props.label] Accessibility label.
 * @param {*} [props.style] Extra style of the root view.
 * @returns {JSX.Element} The mascot.
 */
const Bobo = ({
    size = 120, mood = null, action = null, reaction = null, bursts = [], coverEyes = false, hasError = false,
    celebrate = false, saving = false, greet = false, shake = false, expression = null, pupils, label = 'Bobo', style,
}) => {
    const uid = `bobo${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
    const reduced = useReducedMotion();
    const s = size / ART_W;
    const fromMood = moodToProps(mood);
    const resolvedExpression = expression || fromMood.expression || null;
    const pose = deriveBoboPose({
        action: action || fromMood.action || null,
        reaction,
        expression: resolvedExpression,
        coverEyes,
        hasError: hasError || Boolean(fromMood.hasError),
        celebrate: celebrate || Boolean(fromMood.celebrate),
        saving,
        greet: greet || Boolean(fromMood.greet),
        shake,
    });
    const loving = pose.eyes === 'love';
    const wide = pose.eyes === 'surprised';
    const eyeRx = wide ? 11.5 : 10;
    const eyeRy = wide ? 13.5 : 11.5;
    const pupilR = wide ? 3.6 : 6;
    const showPupils = ['normal', 'sparkle', 'surprised', 'wink'].includes(pose.eyes);
    const rightHappy = pose.eyes === 'happy' || pose.eyes === 'wink';
    const leftHappy = pose.eyes === 'happy';
    const look = pose.thinking ? {x: 3, y: -4} : (pose.sitting ? {x: 0, y: 2} : {x: pupils?.x || 0, y: pupils?.y || 0});
    const paint = (name) => `url(#${uid}-${name})`;
    const moving = !reduced;

    const {id, t} = useMotion(pose.motion, reduced);
    const wag = useLoop(800, moving);
    const breathe = useLoop(1600, moving);
    const sprout = useLoop(1500, moving);
    const sway = useLoop(1600, moving);
    const wave = useLoop(550, moving && pose.armRightWave);
    const blink = useBlink(moving && showPupils && !pose.sitting);
    const pulse = useLoop(400, moving && loving);
    const lookX = useSharedValue(look.x);
    const lookY = useSharedValue(look.y);
    const pop = useSharedValue(0);
    const zz = useLoop(1100, moving && pose.sitting, {reverse: false});

    useEffect(() => {
        lookX.value = withTiming(look.x, {duration: 180});
        lookY.value = withTiming(look.y, {duration: 180});
    }, [look.x, look.y, lookX, lookY]);

    useEffect(() => {
        pop.value = pose.thinking ? withTiming(1, {duration: 400, easing: Easing.out(Easing.back(2))}) : 0;
    }, [pose.thinking, pop]);

    const moodStyle = useAnimatedStyle(() => {
        const m = evaluate(id.value, t.value);

        return {
            transform: [
                {translateX: m.tx * s}, {translateY: m.ty * s}, {rotate: `${m.rot}deg`}, {scaleX: m.sx}, {scaleY: m.sy},
            ],
        };
    });
    const shadowStyle = useAnimatedStyle(() => {
        const m = evaluate(id.value, t.value);

        return {opacity: m.sho, transform: [{scaleX: m.shx}]};
    });
    const headStyle = useAnimatedStyle(() => ({transform: [{rotate: `${evaluate(id.value, t.value).head}deg`}]}));
    const faceStyle = useAnimatedStyle(() => ({opacity: 1 - evaluate(id.value, t.value).back}));
    const backStyle = useAnimatedStyle(() => ({opacity: evaluate(id.value, t.value).back}));
    const eyeShiftStyle = useAnimatedStyle(() => ({transform: [{translateX: evaluate(id.value, t.value).eye * s}]}));
    const armsStyle = useAnimatedStyle(() => ({transform: [{rotate: `${evaluate(id.value, t.value).arms}deg`}]}));
    const tailStyle = useAnimatedStyle(() => ({
        transform: [{rotate: `${-8 + 22 * wag.value}deg`}],
    }));
    const bodyStyle = useAnimatedStyle(() => ({
        transform: [{scaleX: 1 + 0.015 * breathe.value}, {scaleY: 1 - 0.015 * breathe.value}],
    }));
    const sproutStyle = useAnimatedStyle(() => ({transform: [{rotate: `${-6 + 13 * sprout.value}deg`}]}));
    const armRightStyle = useAnimatedStyle(() => ({
        transform: [{rotate: `${pose.armRightWave ? -10 + 38 * wave.value : 6 * sway.value}deg`}],
    }));
    const lidStyle = useAnimatedStyle(() => ({transform: [{scaleY: Math.max(0.001, blink.value)}]}));
    const pupilStyle = useAnimatedStyle(() => ({
        transform: [{translateX: lookX.value * s}, {translateY: lookY.value * s}],
    }));
    const heartStyle = useAnimatedStyle(() => ({transform: [{scale: 1 + 0.06 * pulse.value}]}));
    const bubbleStyle = useAnimatedStyle(() => ({
        opacity: pop.value,
        transform: [{translateY: (1 - pop.value) * 6 * s}, {scale: 0.96 + 0.04 * pop.value}],
    }));
    const zStyle = useAnimatedStyle(() => ({
        opacity: sample([0, 0.3, 1], [0, 1, 0], zz.value),
        transform: [{translateX: 10 * s * zz.value}, {translateY: -16 * s * zz.value}],
    }));
    const arm = (d) => (
        <G>
            <Path d={d} stroke="#EA580C" strokeWidth="12" strokeLinecap="round" fill="none" />
            <Path d={d} stroke="#FDBA74" strokeWidth="3.5" strokeLinecap="round" fill="none" opacity={0.55} transform="translate(-1.5 -2)" />
        </G>
    );
    const hand = (cx, cy, r = 8) => (
        <G>
            <Circle cx={cx} cy={cy} r={r} fill={paint('hand')} />
            <Circle cx={cx - r * 0.3} cy={cy - r * 0.35} r={r * 0.28} fill="#FFFFFF" opacity={0.7} />
        </G>
    );
    const happyArc = (cx) => (
        <Path d={`M${cx - 9.5} 80q9.5-15 19 0`} stroke="#3B1D0E" strokeWidth="4" strokeLinecap="round" fill="none" />
    );
    const unit = (n) => n * s;
    const zFont = (n) => ({position: 'absolute', color: '#FDBA74', fontWeight: '700', fontSize: unit(n)});

    return (
        <View
            accessible
            accessibilityRole="image"
            accessibilityLabel={label}
            style={[{width: size, height: size * ART_H / ART_W, overflow: 'visible'}, style]}
        >
            <Layer s={s} uid={uid} origin={[80, 166]} style={shadowStyle}>
                <Ellipse cx="80" cy="166" rx="48" ry="7" fill={paint('shadow')} />
            </Layer>

            {pose.thinking && (
                <Layer s={s} uid={uid} origin={[122, 44]} style={bubbleStyle}>
                    <Circle cx="122" cy="44" r="4" fill="#FFFFFF" />
                    <Circle cx="133" cy="31" r="6" fill="#FFFFFF" />
                    <Ellipse cx="150" cy="12" rx="17" ry="12" fill="#FFFFFF" />
                    <Circle cx="143" cy="12" r="2.2" fill="#F97316" />
                    <Circle cx="150" cy="12" r="2.2" fill="#F97316" opacity={0.6} />
                    <Circle cx="157" cy="12" r="2.2" fill="#F97316" opacity={0.35} />
                </Layer>
            )}
            {pose.sitting && (
                <Animated.View pointerEvents="none" style={[{position: 'absolute', left: unit(112), top: unit(30), width: unit(30), height: unit(30)}, zStyle]}>
                    <Text style={[zFont(14), {left: 0, top: unit(16)}]}>z</Text>
                    <Text style={[zFont(18), {left: unit(10), top: 0}]}>z</Text>
                </Animated.View>
            )}

            <Frame s={s} origin={[80, 168]} style={moodStyle}>
                <Layer s={s} uid={uid} origin={[118, 118]} style={tailStyle}>
                    <Path d="M116 124c18-2 30-14 30-30 0-6-6-8-9-3-4 9-11 17-24 20z" fill={paint('tail')} />
                    <Circle cx="141" cy="92" r="6" fill={paint('belly')} />
                </Layer>

                <Frame s={s} origin={[80, 150]} style={bodyStyle}>
                    <Layer s={s} uid={uid}>
                        <Ellipse cx="62" cy="160" rx="13" ry="7" fill={paint('foot')} />
                        <Ellipse cx="98" cy="160" rx="13" ry="7" fill={paint('foot')} />
                        <Ellipse cx="58" cy="157.5" rx="6" ry="2.4" fill="#FFFFFF" opacity={0.22} />
                        <Ellipse cx="94" cy="157.5" rx="6" ry="2.4" fill="#FFFFFF" opacity={0.22} />
                        <Path d={BODY_PATH} fill={paint('body')} />
                        <Path d={BODY_PATH} fill="none" stroke={paint('rim')} strokeWidth="2.2" />
                        <Ellipse cx="80" cy="124" rx="30" ry="26" fill={paint('belly')} />
                        <Ellipse cx="74" cy="112" rx="14" ry="6" fill="#FFFFFF" opacity={0.35} transform="rotate(-12 74 112)" />
                    </Layer>

                    <Layer s={s} uid={uid} origin={[80, 104]} style={armsStyle}>
                        {pose.armsCover && (
                            <G>
                                {arm('M40 112c-6-14 2-30 18-36')}
                                {arm('M120 112c6-14-2-30-18-36')}
                            </G>
                        )}
                        {pose.armsNormal && arm('M38 104c-8 8-10 18-6 26')}
                        {pose.armsUp && (
                            <G>
                                {arm('M38 104c-10-8-14-20-12-32')}
                                {hand(26, 70)}
                                {arm('M122 104c10-8 14-20 12-32')}
                                {hand(134, 70)}
                            </G>
                        )}
                        {pose.armThink && arm('M38 104c-8 8-10 18-6 26')}
                    </Layer>
                    {pose.armsNormal && (
                        <Layer s={s} uid={uid} origin={[122, 104]} style={armRightStyle}>
                            {arm('M122 104c10-4 18-12 20-24')}
                            {hand(142, 78)}
                        </Layer>
                    )}

                    <Frame s={s} origin={[80, 120]} style={headStyle}>
                        <Layer s={s} uid={uid} origin={[80, 56]} style={sproutStyle}>
                            <Path d="M80 56c-1-12 1-22 6-30" stroke="#9A3412" strokeWidth="3" strokeLinecap="round" fill="none" />
                            <Path d="M84 36c-15-1-26-10-30-25 15 0 27 9 30 25z" fill={paint('leafA')} />
                            <Path d="M80 30c-6-3-11-8-14-14" stroke="#FFFFFF" strokeWidth="1.4" strokeLinecap="round" fill="none" opacity={0.4} />
                            <Path d="M86 30c4-15 17-23 32-23-2 15-15 23-32 23z" fill={paint('leafB')} />
                            <Path d="M92 24c6-6 12-10 20-13" stroke="#FFFFFF" strokeWidth="1.4" strokeLinecap="round" fill="none" opacity={0.5} />
                        </Layer>

                        <Frame s={s} style={faceStyle}>
                            <Frame s={s} style={eyeShiftStyle}>
                                <Layer s={s} uid={uid}>
                                    {!leftHappy && !loving && pose.eyes !== 'dizzy' && (
                                        <Ellipse cx="66" cy="76" rx={eyeRx} ry={eyeRy} fill={paint('eye')} />
                                    )}
                                    {!rightHappy && !loving && pose.eyes !== 'dizzy' && (
                                        <Ellipse cx="94" cy="76" rx={eyeRx} ry={eyeRy} fill={paint('eye')} />
                                    )}
                                    {pose.eyes === 'dizzy' && (
                                        <G>
                                            <Ellipse cx="66" cy="76" rx="10.5" ry="11.5" fill="#FFFFFF" />
                                            <Ellipse cx="94" cy="76" rx="10.5" ry="11.5" fill="#FFFFFF" />
                                        </G>
                                    )}
                                    {leftHappy && happyArc(66)}
                                    {rightHappy && happyArc(94)}
                                </Layer>

                                {pose.eyes === 'dizzy' && [66, 94].map((cx, index) => (
                                    <Spinner
                                        key={cx}
                                        duration={900}
                                        reverse={index === 1}
                                        enabled={moving}
                                        style={{position: 'absolute', left: unit(cx - 10), top: unit(66), width: unit(20), height: unit(20)}}
                                    >
                                        <Svg width="100%" height="100%" viewBox="-10 -10 20 20">
                                            <Path d={SPIRAL} stroke="#7C2D12" strokeWidth="1.7" strokeLinecap="round" fill="none" />
                                        </Svg>
                                    </Spinner>
                                ))}
                                {loving && (
                                    <Layer s={s} uid={uid} origin={[80, 76]} style={heartStyle}>
                                        <G transform="translate(66 76) scale(1.45)"><Path d={HEART} fill={paint('heart')} stroke="#FFFFFF" strokeWidth="0.8" /></G>
                                        <G transform="translate(94 76) scale(1.45)"><Path d={HEART} fill={paint('heart')} stroke="#FFFFFF" strokeWidth="0.8" /></G>
                                    </Layer>
                                )}

                                {showPupils && (
                                    <Layer s={s} uid={uid} style={pupilStyle}>
                                        <Circle cx="66" cy="77" r={pupilR} fill={paint('pupil')} />
                                        {!rightHappy && <Circle cx="94" cy="77" r={pupilR} fill={paint('pupil')} />}
                                        <Circle cx="68.4" cy="74.4" r={wide ? 1.6 : 2.5} fill="#FFFFFF" />
                                        {!rightHappy && <Circle cx="96.4" cy="74.4" r={wide ? 1.6 : 2.5} fill="#FFFFFF" />}
                                        <Circle cx="64" cy="79.6" r="1.1" fill="#FFFFFF" opacity={0.85} />
                                        {!rightHappy && <Circle cx="92" cy="79.6" r="1.1" fill="#FFFFFF" opacity={0.85} />}
                                    </Layer>
                                )}
                                {showPupils && (
                                    <Layer s={s} uid={uid} origin={[80, 64]} style={lidStyle}>
                                        <Ellipse cx="66" cy="76" rx="10.5" ry="12" fill="#FA7C22" />
                                        {!rightHappy && <Ellipse cx="94" cy="76" rx="10.5" ry="12" fill="#FA7C22" />}
                                    </Layer>
                                )}

                                <Layer s={s} uid={uid}>
                                    {pose.sitting && (
                                        <G>
                                            <Path d="M55.5 77a10.5 12 0 0 1 21 0z" fill="#FA7C22" />
                                            <Path d="M83.5 77a10.5 12 0 0 1 21 0z" fill="#FA7C22" />
                                        </G>
                                    )}
                                    {pose.thinking && (
                                        <G>
                                            <Path d="M58 60l12-2" stroke="#7C2D12" strokeWidth="3" strokeLinecap="round" />
                                            <Path d="M88 57l13 3" stroke="#7C2D12" strokeWidth="3" strokeLinecap="round" />
                                        </G>
                                    )}
                                    {pose.worried && (
                                        <G>
                                            <Path d="M56 61l14 4" stroke="#7C2D12" strokeWidth="3" strokeLinecap="round" />
                                            <Path d="M104 61l-14 4" stroke="#7C2D12" strokeWidth="3" strokeLinecap="round" />
                                        </G>
                                    )}
                                    <Circle cx="53" cy="94" r="8" fill={paint('cheek')} />
                                    <Circle cx="107" cy="94" r="8" fill={paint('cheek')} />
                                    {pose.mouth === 'open' && (
                                        <G>
                                            <Path d={MOUTH_OPEN} fill="#7C2D12" />
                                            <G clipPath={`url(#${uid}-mouth)`}>
                                                <Ellipse cx="80" cy="101" rx="7" ry="4.5" fill="#FB7185" />
                                            </G>
                                        </G>
                                    )}
                                    {pose.mouth === 'smile' && <Path d="M72 95c4 4 12 4 16 0" stroke="#7C2D12" strokeWidth="3" strokeLinecap="round" fill="none" />}
                                    {pose.mouth === 'cat' && <Path d="M70 95c3 5 7 4 10 0c3 4 7 5 10 0" stroke="#7C2D12" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />}
                                    {pose.mouth === 'flat' && <Path d="M73 97c4-2 10-2 14 0" stroke="#7C2D12" strokeWidth="3" strokeLinecap="round" fill="none" />}
                                    {pose.mouth === 'o' && <Ellipse cx="80" cy="98" rx="4" ry="5" fill="#7C2D12" />}
                                </Layer>
                            </Frame>
                        </Frame>

                        {/* Back of the head: fades in while Bobo turns away. */}
                        <Layer s={s} uid={uid} style={backStyle}>
                            <Ellipse cx="80" cy="84" rx="44" ry="38" fill={paint('body')} />
                        </Layer>
                        {/* Glossy highlight on the crown: the main 3D cue. */}
                        <Layer s={s} uid={uid}>
                            <Ellipse cx="62" cy="62" rx="20" ry="7" fill="#FFFFFF" opacity={0.28} transform="rotate(-22 62 62)" />
                            <Circle cx="45" cy="72" r="2.4" fill="#FFFFFF" opacity={0.35} />
                        </Layer>
                    </Frame>

                    {(pose.armsCover || pose.armThink) && (
                        <Layer s={s} uid={uid}>
                            {pose.armsCover && (
                                <G>
                                    {hand(66, 76, 12)}
                                    {hand(94, 76, 12)}
                                </G>
                            )}
                            {pose.armThink && (
                                <G>
                                    {arm('M122 106c10 2 12-10 0-14-6-2-12 0-16 4')}
                                    {hand(100, 100)}
                                </G>
                            )}
                        </Layer>
                    )}
                </Frame>
            </Frame>

            {pose.stars && (
                <View
                    pointerEvents="none"
                    style={{position: 'absolute', left: unit(80), top: unit(38), width: 0, height: 0, transform: [{scaleY: 0.32}]}}
                >
                    <Spinner duration={1600} enabled={moving} style={{width: 0, height: 0}}>
                        {[[44, 0, '#FBBF24'], [-22, 38, '#FDE68A'], [-22, -38, '#FBBF24']].map(([x, y, color]) => (
                            <View key={`${x}:${y}`} style={{position: 'absolute', left: unit(x - 8), top: unit(y - 8), width: unit(16), height: unit(16)}}>
                                <Svg width="100%" height="100%" viewBox="-8 -8 16 16">
                                    <Path d={STAR} fill={color} />
                                </Svg>
                            </View>
                        ))}
                    </Spinner>
                </View>
            )}

            {bursts.map((item) => (
                <View key={item.id} pointerEvents="none" style={{position: 'absolute', left: 0, top: 0, width: unit(ART_W), height: unit(ART_H)}}>
                    {PARTICLES.map((spec) => (
                        <Particle key={`${spec.x}-${spec.y}`} spec={spec} kind={item.kind} uid={uid} s={s} reduced={reduced} />
                    ))}
                </View>
            ))}
        </View>
    );
};

export default memo(Bobo);
